import { DataLayerAction } from '@corratech/pylot-tag-manager'
import { getInitScriptContent } from './utils'
import { useStoreConfig } from 'config'
import { getApplyMapping } from './mapping/getApplyMapping'
import { GTMActionOverride, GTMEventDataMapping } from './GTM.d'
import { useDataLayerData } from './hooks/useDataLayerData'
import { useEventQueue } from './hooks/useEventQueue'
import './utils/helper/data-layer-helper'
import { getEventDataConsumer } from './utils/getEventDataConsumer'
import { useRouter } from 'next/router'

declare global {
    interface Window {
        dataLayer?: Array<unknown>
    }
}

type GTMData = {
    gtmHandler: DataLayerAction
    gtmInitScript: string
    gtmNoScript: string
}

export const useGTM = (
    mappingOverride: GTMEventDataMapping = {},
    actionOverride: GTMActionOverride = {},
    eventQueueBeforeRules = {},
    eventQueueAfterRules = {},
    delayToNextPageEvents = []
): GTMData => {
    const {
        seo: {
            tagManager: { gtm }
        }
    } = useStoreConfig()
    const isEnabled = gtm && gtm.enabled
    const ID = isEnabled && gtm?.containerId ? gtm.containerId : ''
    const { initScript, noScript } = getInitScriptContent(ID)

    const eventQueue = useEventQueue(
        eventQueueBeforeRules,
        eventQueueAfterRules,
        delayToNextPageEvents
    )
    const { getStoredData, addStoredData, resetStoredData } = useDataLayerData()
    const router = useRouter()
    const consumeEventData = getEventDataConsumer(
        eventQueue,
        getStoredData,
        addStoredData,
        resetStoredData,
        router
    )

    const applyMapping = getApplyMapping(mappingOverride)
    const handleDataLayerAction: DataLayerAction = (action) => {
        if (!isEnabled) {
            return
        }
        let mappedData = applyMapping(action)
        if (action.type && typeof actionOverride[action.type] === 'function') {
            // Data, processed by an action override does not need 'type'
            mappedData = {
                type: '',
                data: actionOverride[action.type](
                    mappedData.data,
                    addStoredData
                )
            }
        }
        consumeEventData(mappedData)
    }

    return {
        gtmHandler: handleDataLayerAction,
        gtmInitScript: isEnabled ? initScript : '',
        gtmNoScript: isEnabled ? noScript : ''
    }
}
